/**
 * R2 upload helper — web port of the mobile r2Upload.
 *
 * Flow:
 *   1. Call the `r2PresignedUploadUrl` Cloud Function with
 *      `{ contentType, kind, refId, orgId, estimatedBytes }`.
 *   2. Backend returns a 5-minute presigned PUT URL.
 *   3. PUT the file to that URL with the matching `Content-Type`.
 *   4. Backend returns the stable `publicUrl` we save to Firestore.
 *
 * Same callable as mobile — no new backend surface, no new bucket,
 * no new rule. Uploads count against the org's R2 storage cap on
 * the server side automatically.
 */
import { httpsCallable } from 'firebase/functions';

import { functions } from '@/lib/firebase';

export type R2Kind =
  | 'project_cover'
  | 'task_photo'
  | 'task_update'
  | 'transaction'
  | 'laminate'
  | 'dpr'
  | 'whiteboard_thumb'
  | 'design'
  | 'studio_cover'
  | 'studio_logo'
  | 'feedback_screenshot';

export type UploadFileToR2Args = {
  /** Browser File object selected from <input type="file"> */
  file: File;
  /** Logical category — controls storage path on the bucket. */
  kind: R2Kind;
  /** Owning entity id (task id, project id, etc.) */
  refId: string;
  /** Org id — drives the storage paywall check server-side. */
  orgId: string;
};

export type UploadFileToR2Result = {
  publicUrl: string;
  key: string;
};

type CallableInput = {
  contentType: string;
  kind: R2Kind;
  refId: string;
  ext?: string;
  orgId?: string;
  estimatedBytes?: number;
};

type CallableOutput = {
  uploadUrl: string;
  key: string;
  publicUrl: string;
};

/** "image/jpeg" → "jpg". Used only when the File object has no
 *  detectable name; modern browsers always set `file.name`. */
export function extFromMime(mime: string): string {
  if (mime === 'image/jpeg' || mime === 'image/jpg') return 'jpg';
  if (mime === 'image/png') return 'png';
  if (mime === 'image/gif') return 'gif';
  if (mime === 'image/webp') return 'webp';
  if (mime === 'image/heic' || mime === 'image/heif') return 'heic';
  if (mime === 'application/pdf') return 'pdf';
  return 'bin';
}

export function extFromFile(file: File): string {
  const dot = file.name.lastIndexOf('.');
  if (dot >= 0 && dot < file.name.length - 1) {
    return file.name.slice(dot + 1).toLowerCase();
  }
  return extFromMime(file.type);
}

/**
 * Upload one file. Returns the stable public URL ready to write to
 * Firestore. Surfaces a friendly error if the cap check denies the
 * upload server-side.
 */
export async function uploadFileToR2(
  args: UploadFileToR2Args,
): Promise<UploadFileToR2Result> {
  const { file, kind, refId, orgId } = args;
  if (!file) throw new Error('uploadFileToR2: file is required');
  if (!refId) throw new Error('uploadFileToR2: refId is required');
  if (!orgId) throw new Error('uploadFileToR2: orgId is required');
  const contentType = file.type || 'application/octet-stream';

  // 1) Ask the backend for a presigned PUT URL.
  const fn = httpsCallable<CallableInput, CallableOutput>(
    functions,
    'r2PresignedUploadUrl',
  );
  const { data } = await fn({
    contentType,
    kind,
    refId,
    ext: extFromFile(file),
    orgId,
    estimatedBytes: file.size,
  });

  // 2) PUT the bytes. The presigned URL enforces Content-Type, so
  //    we MUST send the same header here that we sent above.
  const put = await fetch(data.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!put.ok) {
    throw new Error(
      `R2 upload failed (HTTP ${put.status} ${put.statusText})`,
    );
  }

  return { publicUrl: data.publicUrl, key: data.key };
}

/** Upload N files in parallel. Returns the public URLs in input
 *  order. If any file fails, all in-flight uploads are awaited so
 *  the caller gets a clean rejection (no orphan promises). */
export async function uploadFilesToR2(
  args: Omit<UploadFileToR2Args, 'file'> & { files: File[] },
): Promise<string[]> {
  const results = await Promise.all(
    args.files.map((file) =>
      uploadFileToR2({
        file,
        kind: args.kind,
        refId: args.refId,
        orgId: args.orgId,
      }),
    ),
  );
  return results.map((r) => r.publicUrl);
}

/**
 * Delete an R2 object via the `r2DeleteObject` Cloud Function.
 *
 * The backend verifies the caller is a project member, deletes the
 * object from R2, decrements the project's storage counter, and
 * records the event in the audit log. Same callable mobile uses
 * for its delete flows.
 *
 * Safe to no-op-and-warn on failure — the worst case is an orphan
 * blob in R2 that gets garbage-collected by the next storage audit
 * (the Firestore doc that referenced it is already gone).
 */
export type DeleteFromR2Args = {
  projectId: string;
  key: string;
  kind: R2Kind;
  refId: string;
  sizeBytes?: number;
  contentType?: string;
};

export async function deleteFromR2(args: DeleteFromR2Args): Promise<void> {
  if (!args.key) return; // nothing to delete
  const fn = httpsCallable<DeleteFromR2Args, { ok: true }>(
    functions,
    'r2DeleteObject',
  );
  await fn(args);
}
